import { Page, PageSkeleton } from '@/components';
import { useApi } from '@/api';
import { useSearchParams } from '@/hooks';
import { useParams } from 'react-router';
import {
  Flex,
  KpiCard,
  WalletAddress,
  Box,
  KpiSkeleton,
  TableSkeleton
} from '@blockanalitica/ui';
import { useNetwork } from '@/hooks';
import WalletActivity from './WalletActivity';
import CollateralsTable from './CollateralsTable';

type WalletResponse = {
  address: string;
  name: string;
  market_symbol: string;
  market_address: string;
  market_underlying_address: string;
  collateral: string | null;
  wallet_address: string;
  borrow: string | null;
  collaterals: {
    symbol: string;
    address: string;
    price_usd: string | null;
    collateral_usd: string | null;
    collateral_balance: string | null;
  }[];
};

export default function WalletPage() {
  const network = useNetwork();
  const { marketAddress, walletAddress } = useParams() as {
    marketAddress: string;
    walletAddress: string;
  };
  const [daysAgo] = useSearchParams('days_ago', '1');

  const [data, error] = useApi<WalletResponse>({
    url: `/markets/${marketAddress}/wallets/${walletAddress}/`,
    queryParams: {
      days_ago: daysAgo
    }
  });

  if (error) {
    throw error;
  }

  if (!data) {
    return (
      <PageSkeleton>
        <Flex gap={['s', 'xs']} flexDirection={['column', 'row']}>
          <KpiSkeleton />
          <KpiSkeleton />
          <KpiSkeleton />
        </Flex>
        <TableSkeleton rows={10} />
        <Flex gap={['s', 'xs']} flexDirection={['column', 'row']}>
          <KpiSkeleton />
          <KpiSkeleton />
        </Flex>
      </PageSkeleton>
    );
  }

  return (
    <Page
      title={
        <WalletAddress
          options={{
            name: 'Borrower',
            showCopyBtn: true,
            showEthScanLink: true,
            showDebankLink: true,
            sizes: {
              blockie: '6'
            }
          }}
          address={data.wallet_address}
        />
      }>
      <Flex gap={['s', 'xs']} flexDirection={['column', 'row']}>
        <KpiCard
          title="Collateral"
          // @ts-ignore
          data={data}
          field="collateral"
          options={{
            valueOptions: {
              currencyValueOptions: {
                currencySize: '3',
                currency: {
                  name: data.market_symbol,
                  address: data.market_underlying_address,
                  network: network
                },
                notation: 'compact'
              }
            }
          }}
        />
        <KpiCard
          title="Health Rate"
          // @ts-ignore
          data={data}
          field="health_rate"
          options={{
            valueOptions: {
              currencyValueOptions: {
                notation: 'compact',
                nullable: <Box fontSize="5">&infin;</Box>
              }
            }
          }}
        />
        <KpiCard
          title="Borrow"
          // @ts-ignore
          data={data}
          field="borrow"
          options={{
            valueOptions: {
              currencyValueOptions: {
                currencySize: '3',
                currency: {
                  name: data.market_symbol,
                  address: data.market_underlying_address,
                  network: network
                },
                notation: 'compact'
              }
            }
          }}
        />
      </Flex>

      <CollateralsTable
        marketAddress={marketAddress}
        walletAddress={walletAddress}
      />

      <Flex gap={['s', 'xs']} flexDirection={['column', 'row']}>
        <KpiCard
          title="Drop to liquidation"
          // @ts-ignore
          data={data}
          field="drop"
          options={{
            percentage: true,
            notation: 'compact'
          }}
        />
        <KpiCard
          title="Collateral ratio"
          // @ts-ignore
          data={data}
          field="collateral_ratio"
          options={{
            percentage: true,
            notation: 'compact'
          }}
        />
      </Flex>
      <WalletActivity
        marketAddress={marketAddress}
        walletAddress={walletAddress}
      />
    </Page>
  );
}
